<script module lang="ts">
  let seq = 0;
</script>

<script lang="ts">
  /**
   * Compose screen (spec §11). Full-page `.compose` layout by default; with `docked` it renders
   * the `section.dock` chrome itself (collapse chevron + the full-page/dock toggle of spec §1.5
   * item 2, persisted under `tik.composeFull`) with the same compose inside.
   *
   * Logic comes from `$lib/mail/compose` (addresses, cap, fingerprint, FormData) and
   * `./editor` (selection, execCommand, cid images, keyboard lift); this file owns state, the
   * autosave debounce, the send flow and the DOM.
   */
  import { onMount, tick, untrack } from 'svelte';
  import { api } from '$lib/api/client';
  import { kvGet } from '$lib/db';
  import { getLang, t } from '$lib/i18n';
  import {
    ATTACH_LIMIT_ERROR,
    AUTOSAVE_MS,
    INLINE_IMAGE_MAX_BYTES,
    RCPT_CONFIRM_AT,
    RCPT_NOTE_AT,
    autosaveFingerprint,
    buildSendForm,
    exceedsAttachCap,
    fileForPaste,
    hasUnsavedContent,
    identityLabel,
    isDraftWorthSaving,
    newCid,
    normalizeLink,
    recipientCount,
    splitAddresses,
    templateToHtml,
    totalSize,
  } from '$lib/mail/compose';
  import { sanitizeCompose, textToHtml } from '$lib/mail/sanitize';
  import { confirm, prompt, store } from '$lib/store';
  import type { ComposeDraft, ForwardPart } from '$lib/types';
  import { debounce } from '$lib/util/debounce';
  import { tooltip } from '$lib/util/dom';
  import { ccBccChord, isSendChord } from '$lib/util/keyboard';
  import Icon from '../ui/Icon.svelte';
  import AddressField from './AddressField.svelte';
  import FileChips from './FileChips.svelte';
  import ScheduleMenu from './ScheduleMenu.svelte';
  import TemplateMenu from './TemplateMenu.svelte';
  import Toolbar from './Toolbar.svelte';
  import { fullButtonLabel, readComposeFull, writeComposeFull } from './_local';
  import {
    cidImageHtml,
    clearFileInput,
    droppedFiles,
    fileInputFiles,
    editorText,
    execCommand,
    insertHtml,
    insertImage,
    isImageFile,
    keyboardLift,
    mentionsAttachmentIn,
    objectUrl,
    pastedImages,
    placeCaretAtStart,
    readAsDataUrl,
    restoreSelection,
    revokeObjectUrl,
    selectionIn,
    type EditorCommand,
    type SavedRange,
  } from './editor';

  let { draft, docked = false }: { draft: ComposeDraft; docked?: boolean } = $props();

  // The draft seeds the state once; a new draft means a new Compose (the router keys on it).
  const initial: ComposeDraft = untrack(() => draft) ?? {};

  let to = $state(splitAddresses(initial.to));
  let cc = $state(splitAddresses(initial.cc));
  let bcc = $state(splitAddresses(initial.bcc));
  let showCc = $state(!!initial.cc || !!initial.bcc);
  let showBcc = $state(!!initial.bcc);
  let subject = $state(initial.subject ?? '');
  const initialText = initial.text ?? '';
  let files = $state<File[]>([]);
  let fwdParts = $state<ForwardPart[]>([...(initial.fwdFrom?.parts ?? [])]);
  let cidMap = $state<{ name: string; cid: string }[]>([]);
  let sending = $state(false);
  let error = $state('');
  let draftUid = $state<number | undefined>(initial.draftUid);
  let identities = $state<{ name?: string; sig?: string }[]>([]);
  let identityIdx = $state(0);
  let schedOpen = $state(false);
  let tplOpen = $state(false);
  let dockMin = $state(false);
  let full = $state(readComposeFull());

  let editorEl = $state<HTMLElement | undefined>();
  let formEl = $state<HTMLElement | undefined>();
  let toolbarEl = $state<HTMLElement | undefined>();
  let ccField = $state<ReturnType<typeof AddressField> | undefined>();
  let bccField = $state<ReturnType<typeof AddressField> | undefined>();

  /** Last selection seen inside the editor (restored before every toolbar command). */
  let savedRange: SavedRange = null;
  let lastFingerprint = '';
  /** Set once the message left: no autosave, no beforeunload guard. */
  let closed = false;
  let imageSeq = 0;
  const blobUrls: string[] = [];

  const id = `tik-cmp-${++seq}`;
  const schedId = `${id}-sched`;
  const tplId = `${id}-tpl`;
  const titleId = `${id}-title`;

  const rcpt = $derived(recipientCount({ to, cc, bcc }));
  const canSend = $derived(!sending && to.length > 0);
  const fullLabel = $derived(fullButtonLabel(full, getLang()));

  // ---- autosave --------------------------------------------------------------------------------

  const autosave = debounce(() => void saveDraft(), AUTOSAVE_MS);
  const scheduleSave = () => autosave();

  async function saveDraft(): Promise<void> {
    if (closed || store.offline || !editorEl) return;
    const html = editorEl.innerHTML;
    const text = editorText(editorEl);
    const fingerprint = autosaveFingerprint({ to, cc, bcc, subject, html });
    if (fingerprint === lastFingerprint || !isDraftWorthSaving({ to, subject, text })) return;
    lastFingerprint = fingerprint;
    try {
      const r = await api.saveDraft({
        to: to.join(', '),
        cc: cc.join(', '),
        bcc: bcc.join(', '),
        subject,
        text,
        html: sanitizeCompose(html),
        draftUid,
      });
      if (r.uid) draftUid = r.uid;
      store.showToast('draftSaved');
    } catch {
      /* spec §11: draft errors are silently ignored */
    }
  }

  // ---- identities + initial editor content ---------------------------------------------------

  async function loadIdentities(): Promise<void> {
    let list: { name?: string; sig?: string }[] = [];
    let signature = '';
    try {
      const parsed: unknown = JSON.parse((await kvGet('identities')) || '[]');
      list = Array.isArray(parsed) ? (parsed as { name?: string; sig?: string }[]) : [];
      signature = list[0]?.sig ?? (await kvGet('signature')) ?? '';
    } catch {
      list = [];
    }
    identities = list;
    const el = editorEl;
    if (!el) return;
    if (initial.html) {
      if (initial.draftUid) {
        el.innerHTML = sanitizeCompose(initial.html);
      } else {
        el.innerHTML = '<div><br></div>' + (signature ? textToHtml('\n' + signature) : '') + sanitizeCompose(initial.html);
        placeCaretAtStart(el);
      }
    } else {
      const text = initial.text ?? (signature ? '\n\n' + signature : '');
      if (text) el.innerHTML = textToHtml(text);
    }
  }

  // ---- attachments -----------------------------------------------------------------------------

  /** Add local files under the 25 MB cap; false (and the `≤ 25MB` error) when they do not fit. */
  function addFiles(list: readonly File[]): boolean {
    if (exceedsAttachCap(files, totalSize(list))) {
      error = ATTACH_LIMIT_ERROR;
      return false;
    }
    files = [...files, ...list];
    return true;
  }

  /** Pasted/dropped image: attached with a cid and shown in place as `<img src=blob data-tik-cid>`. */
  function attachInline(file: File): void {
    const cid = newCid();
    const named = fileForPaste(file, ++imageSeq);
    files = [...files, named];
    cidMap = [...cidMap, { name: named.name, cid }];
    const url = objectUrl(named);
    if (url) blobUrls.push(url);
    if (editorEl) {
      restoreSelection(editorEl, savedRange);
      insertHtml(editorEl, cidImageHtml(url, cid));
    }
  }

  function onPaste(ev: Event): void {
    const images = pastedImages(ev);
    if (!images.length) return;
    ev.preventDefault();
    for (const f of images) {
      if (exceedsAttachCap(files, f.size)) {
        error = ATTACH_LIMIT_ERROR;
        return;
      }
      attachInline(f);
    }
    scheduleSave();
  }

  function onDrop(ev: Event): void {
    ev.preventDefault();
    const list = droppedFiles(ev);
    if (!list.length) return;
    for (const f of list) {
      if (exceedsAttachCap(files, f.size)) {
        error = ATTACH_LIMIT_ERROR;
        return;
      }
      if (isImageFile(f)) attachInline(f);
      else files = [...files, f];
    }
    scheduleSave();
  }

  function onAttach(ev: Event): void {
    const list = fileInputFiles(ev);
    if (!list.length) return;
    if (addFiles(list)) clearFileInput(ev);
  }

  // ---- toolbar ---------------------------------------------------------------------------------

  function exec(command: EditorCommand | 'createLink', value?: string): void {
    if (!editorEl) return;
    restoreSelection(editorEl, savedRange);
    execCommand(command, value);
    scheduleSave();
  }

  async function addLink(): Promise<void> {
    const url = await prompt({ title: t('linkPrompt'), placeholder: 'https://…', ok: t('ok') });
    if (url) exec('createLink', normalizeLink(url));
  }

  /** Toolbar image: ≤ 1.5 MB inline as a data URL, larger ones become a regular attachment. */
  async function onImage(file: File): Promise<void> {
    if (file.size > INLINE_IMAGE_MAX_BYTES) {
      addFiles([file]);
      return;
    }
    const src = await readAsDataUrl(file);
    if (!editorEl) return;
    restoreSelection(editorEl, savedRange);
    insertImage(editorEl, src);
    scheduleSave();
  }

  function toggleTemplates(): void {
    tplOpen = !tplOpen;
    if (tplOpen) void store.loadTemplates();
  }

  function insertTemplate(body: string): void {
    tplOpen = false;
    if (!editorEl) return;
    restoreSelection(editorEl, savedRange);
    insertHtml(editorEl, templateToHtml(body));
    scheduleSave();
  }

  async function saveAsTemplate(): Promise<void> {
    const text = editorText(editorEl).trim();
    if (!text) {
      store.showToast('actionFailed');
      return;
    }
    const name = await prompt({ title: t('saveAsTemplate'), value: subject || '', placeholder: t('templateName'), ok: t('save') });
    if (name) await store.saveTemplate(name, text);
  }

  // ---- cc / bcc --------------------------------------------------------------------------------

  async function revealCc(): Promise<void> {
    showCc = true;
    await tick();
    ccField?.focus();
  }

  async function revealBcc(): Promise<void> {
    showBcc = true;
    await tick();
    bccField?.focus();
  }

  // ---- send ------------------------------------------------------------------------------------

  async function send(sendAt?: number): Promise<void> {
    error = '';
    if (store.offline) {
      error = t('offlineSend');
      return;
    }
    if (!to.length || sending) return;
    sending = true;
    try {
      const attachmentCount = files.length + fwdParts.length;
      if (mentionsAttachmentIn(editorEl, attachmentCount) && !(await confirm({ title: t('confirmTitle'), body: t('attachMissing'), ok: t('send') }))) return;
      if (rcpt >= RCPT_CONFIRM_AT && !(await confirm({ title: t('confirmTitle'), body: t('manyRcpt', { n: rcpt }), ok: t('send') }))) return;
      const fd = buildSendForm({
        to,
        cc,
        bcc,
        subject,
        text: editorEl ? editorText(editorEl) : initialText,
        editorHtml: editorEl?.innerHTML ?? '',
        inReplyTo: initial.inReplyTo,
        references: initial.references,
        fromName: identities[identityIdx]?.name,
        files,
        cidMap: $state.snapshot(cidMap),
        fwdFrom: initial.fwdFrom && fwdParts.length ? { folder: initial.fwdFrom.folder, uid: initial.fwdFrom.uid, parts: $state.snapshot(fwdParts) } : undefined,
        sendAt,
        undoSec: store.undoSec,
      });
      const r = await api.send(fd);
      autosave.cancel();
      closed = true;
      if (draftUid) api.deleteDraft(draftUid).catch(() => undefined);
      if (r.scheduled) store.showToast('scheduled');
      else if (r.queued && r.id) store.startSendUndo(r.id, r.undoSec ?? store.undoSec);
      else store.showToast('sent');
      store.closeCompose();
      void store.refresh();
    } catch {
      error = t('sendFailed');
    } finally {
      sending = false;
    }
  }

  // ---- dock chrome -----------------------------------------------------------------------------

  function toggleFull(): void {
    full = !full;
    writeComposeFull(full);
  }

  // ---- lifecycle -------------------------------------------------------------------------------

  function onWindowKey(ev: KeyboardEvent): void {
    if (isSendChord(ev)) {
      ev.preventDefault();
      if (canSend) void send();
      return;
    }
    const which = ccBccChord(ev);
    if (which === 'cc') {
      ev.preventDefault();
      void revealCc();
    } else if (which === 'bcc') {
      ev.preventDefault();
      void revealBcc();
    }
  }

  /** Capture-phase Escape: close our own menus before the shell's handler closes the dock. */
  function onEscapeCapture(ev: KeyboardEvent): void {
    if (ev.key !== 'Escape' || (!schedOpen && !tplOpen)) return;
    ev.preventDefault();
    ev.stopPropagation();
    schedOpen = false;
    tplOpen = false;
  }

  onMount(() => {
    void loadIdentities();
    const onSelection = () => {
      if (!editorEl) return;
      const range = selectionIn(editorEl);
      if (range) savedRange = range;
    };
    const onUnload = (ev: Event) => {
      if (!closed && hasUnsavedContent({ to, subject, text: editorText(editorEl) })) ev.preventDefault();
    };
    // Files dropped anywhere on the form are attached (the editor has its own handler too).
    const onDragOver = (ev: Event) => ev.preventDefault();
    const form = formEl;
    form?.addEventListener('dragover', onDragOver);
    form?.addEventListener('drop', onDrop);
    document.addEventListener('selectionchange', onSelection);
    window.addEventListener('keydown', onWindowKey);
    window.addEventListener('keydown', onEscapeCapture, true);
    window.addEventListener('beforeunload', onUnload);
    const unlift = keyboardLift(() => toolbarEl);
    return () => {
      form?.removeEventListener('dragover', onDragOver);
      form?.removeEventListener('drop', onDrop);
      document.removeEventListener('selectionchange', onSelection);
      window.removeEventListener('keydown', onWindowKey);
      window.removeEventListener('keydown', onEscapeCapture, true);
      window.removeEventListener('beforeunload', onUnload);
      unlift();
      autosave.cancel();
      for (const url of blobUrls) revokeObjectUrl(url);
    };
  });
</script>

{#snippet body()}
  <div class="compose">
    <header class="bar">
      <button type="button" class="ghost" aria-label={t('discard')} use:tooltip onclick={() => store.closeCompose()}><Icon name="x" size={20} /></button>
      <h2 id={titleId}>{t('compose')}</h2>
      <span class="grow"></span>
      <button
        type="button"
        class="sched-btn"
        aria-haspopup="menu"
        aria-label={t('schedule')}
        aria-controls={schedOpen ? schedId : undefined}
        aria-expanded={schedOpen}
        disabled={!canSend}
        use:tooltip
        onclick={() => (schedOpen = !schedOpen)}><Icon name="moon" size={16} /></button>
      {#if schedOpen}
        <ScheduleMenu
          id={schedId}
          onpick={(at) => {
            schedOpen = false;
            void send(at);
          }}
          onclose={() => (schedOpen = false)}
        />
      {/if}
      <button type="button" class="send" disabled={!canSend} onclick={() => void send()}><Icon name="send" size={16} /> {sending ? t('sending') : t('send')}</button>
    </header>
    <div class="form" bind:this={formEl}>
      {#if identities.length > 1}
        <label class="row">
          <span>{t('from')}</span>
          <select bind:value={identityIdx}>
            {#each identities as ident, i (i)}
              <option value={i}>{identityLabel(ident.name, store.user ?? '')}</option>
            {/each}
          </select>
        </label>
      {/if}
      <div class="row arow">
        <AddressField bind:addrs={to} label={t('to')} kind="to" onchange={scheduleSave} />
        {#if !showCc}<button type="button" class="cc-btn" onclick={() => void revealCc()}>{t('cc')}</button>{/if}
        {#if !showBcc}<button type="button" class="cc-btn" onclick={() => void revealBcc()}>{t('bcc')}</button>{/if}
      </div>
      {#if showCc}
        <div class="row arow"><AddressField bind:this={ccField} bind:addrs={cc} label={t('cc')} kind="cc" onchange={scheduleSave} /></div>
      {/if}
      {#if showBcc}
        <div class="row arow"><AddressField bind:this={bccField} bind:addrs={bcc} label={t('bcc')} kind="bcc" onchange={scheduleSave} /></div>
      {/if}
      {#if rcpt >= RCPT_NOTE_AT}
        <p class="rcpt-note" role="status">{t('rcptCount', { n: rcpt })}</p>
      {/if}
      <label class="row">
        <span>{t('subject')}</span>
        <input type="text" dir="auto" autocomplete="off" bind:value={subject} oninput={scheduleSave} />
      </label>
      <Toolbar
        bind:el={toolbarEl}
        {tplOpen}
        tplMenuId={tplId}
        onCommand={(c) => exec(c)}
        onLink={() => void addLink()}
        onImage={(f) => void onImage(f)}
        onTemplate={toggleTemplates}
        onSaveTemplate={() => void saveAsTemplate()}
      />
      <div class="tpl-anchor">
        {#if tplOpen}<TemplateMenu id={tplId} onpick={insertTemplate} onclose={() => (tplOpen = false)} />{/if}
      </div>
      <div
        class="editor"
        contenteditable="true"
        dir="auto"
        role="textbox"
        tabindex="0"
        aria-multiline="true"
        aria-labelledby={titleId}
        bind:this={editorEl}
        oninput={scheduleSave}
        onpaste={onPaste}
        ondragover={(ev) => ev.preventDefault()}
        ondrop={onDrop}
      ></div>
      <FileChips {files} fwd={fwdParts} onremove={(i) => (files = files.filter((_, j) => j !== i))} onremovefwd={(i) => (fwdParts = fwdParts.filter((_, j) => j !== i))} />
      {#if error}<p class="err" role="alert">{error}</p>{/if}
      <footer class="foot">
        <label class="attach"><Icon name="clip" size={18} /> {t('attach')} <input class="filein" type="file" multiple onchange={onAttach} /></label>
      </footer>
    </div>
  </div>
{/snippet}

{#if docked}
  <section class="dock" class:min={dockMin} class:tik-full={full} aria-label={t('compose')}>
    <div class="dock-bar">
      <span class="dock-title">{t('compose')}</span>
      <button type="button" class="tik-fullbtn" data-mode={full ? 'full' : 'dock'} aria-pressed={full} aria-label={fullLabel} title={fullLabel} onclick={toggleFull}>
        {#if full}
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="4 14 10 14 10 20" /><polyline points="20 10 14 10 14 4" /><line x1="14" y1="10" x2="21" y2="3" /><line x1="3" y1="21" x2="10" y2="14" /></svg>
        {:else}
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" /><line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" /></svg>
        {/if}
      </button>
      <button type="button" class="ghost" aria-expanded={!dockMin} aria-label={dockMin ? t('dockExpand') : t('dockMin')} use:tooltip onclick={() => (dockMin = !dockMin)}>
        <span class="dock-chev" class:up={dockMin}><Icon name="chev" size={18} /></span>
      </button>
    </div>
    <div class="dock-body">{@render body()}</div>
  </section>
{:else}
  {@render body()}
{/if}

<style>
  /* ===== component: svelte-lveo7m (Compose) ===== */
  .compose {
    height: 100%;
    display: grid;
    grid-template-rows: auto 1fr;
    background: var(--surface-card);
    min-height: 0;
  }

  .bar {
    position: relative;
    display: flex;
    align-items: center;
    gap: var(--space-3);
    height: 56px;
    padding-inline: var(--space-3);
    border-bottom: 1px solid var(--border-subtle);
  }

  .bar h2 {
    font-size: var(--text-lg);
    color: var(--text-strong);
  }

  .grow {
    flex: 1;
  }

  .tpl-anchor {
    position: relative;
    block-size: 0;
  }

  .sched-btn {
    border: 1px solid var(--border-default);
    border-radius: var(--radius-control);
    background: var(--surface-card);
    color: var(--text-body);
    padding: 0 var(--space-3);
    min-height: var(--control-md);
    cursor: pointer;
    margin-inline-end: var(--space-2);
    transition: background var(--duration-fast) var(--ease-standard);
  }

  .sched-btn:hover:not(:disabled) {
    background: var(--surface-hover);
  }

  .sched-btn:disabled {
    opacity: 0.42;
    cursor: default;
  }

  .send {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    background: var(--blue-500);
    color: var(--text-on-accent);
    height: var(--control-lg);
    padding-inline: var(--space-5);
    border-radius: var(--radius-control);
    font-weight: var(--weight-medium);
    transition: background var(--duration-fast) var(--ease-standard);
  }

  .send:hover {
    background: var(--blue-600);
  }

  .send:active {
    transform: scale(0.99);
  }

  .send:disabled {
    opacity: 0.42;
  }

  .ghost {
    padding: var(--space-2);
    border-radius: var(--radius-control);
    color: var(--text-muted);
  }

  .ghost:hover {
    background: var(--surface-sunken);
  }

  .ghost:active {
    background: var(--surface-sunken);
    transform: scale(0.96);
  }

  .form {
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior-y: contain;
  }

  /* `.toolbar` and `.files` are rendered by child components (Toolbar, FileChips). */
  .row,
  .form > :global(.toolbar),
  .tpl-anchor,
  .form > :global(.files),
  .err,
  .foot {
    flex: none;
  }

  .row {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    border-bottom: 1px solid var(--border-subtle);
    padding-inline: var(--space-4);
    min-height: var(--control-xl);
  }

  .row span {
    color: var(--text-faint);
    font-size: var(--text-sm);
    min-width: 44px;
  }

  .row input {
    flex: 1;
    width: 0;
    border: 1px solid transparent;
    background: none;
    height: var(--control-lg);
    color: var(--text-body);
    outline: none;
    min-width: 0;
    border-radius: var(--radius-control);
  }

  .row select {
    flex: 1;
    width: 0;
    border: 1px solid transparent;
    background: none;
    height: var(--control-lg);
    color: var(--text-body);
    outline: none;
    min-width: 0;
    font: inherit;
    border-radius: var(--radius-control);
  }

  .row input:focus-visible,
  .row select:focus-visible {
    border-color: var(--focus-border);
    box-shadow: var(--ring-focus);
  }

  .arow {
    align-items: flex-start;
  }

  .cc-btn {
    color: var(--text-faint);
    font-size: var(--text-sm);
    padding: var(--space-3) var(--space-2);
  }

  @media (max-width: 900px) and (pointer: coarse) {
    .sched-btn {
      min-width: 44px;
      min-height: 44px;
      height: auto;
    }
  }

  .attach:focus-within {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }

  .filein {
    position: absolute;
    inline-size: 1px;
    block-size: 1px;
    opacity: 0;
  }

  .editor {
    flex: 1 0 auto;
    min-height: 38dvh;
    padding: var(--space-4);
    outline: none;
    border: 1px solid transparent;
    border-radius: var(--radius-control);
    line-height: var(--leading-body);
    color: var(--text-body);
    overflow-wrap: anywhere;
  }

  .editor:focus-visible {
    border-color: var(--focus-border);
    box-shadow: var(--ring-focus);
  }

  /* editor children are user content, outside Svelte's scoping */
  .editor :global(img) {
    max-width: 100%;
    height: auto;
    border-radius: var(--radius-sm);
  }

  .editor :global(blockquote) {
    border-inline-start: 3px solid var(--border-strong);
    margin-inline: 0;
    padding-inline-start: 12px;
    color: var(--text-muted);
  }

  .err {
    color: var(--danger-500);
    font-size: var(--text-sm);
    padding-inline: var(--space-4);
  }

  .foot {
    border-top: 1px solid var(--border-subtle);
    padding: var(--space-2) var(--space-3);
    padding-bottom: calc(var(--space-2) + env(safe-area-inset-bottom));
  }

  .attach {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    color: var(--text-muted);
    font-size: var(--text-sm);
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-control);
    cursor: pointer;
  }

  .attach:hover {
    background: var(--surface-sunken);
  }

  @media (max-width: 900px) {
    .form {
      padding-bottom: calc(var(--control-lg) + var(--space-2) + env(safe-area-inset-bottom));
    }
  }

  .rcpt-note {
    margin: 0 0 var(--space-2);
    font-size: var(--text-xs);
    color: var(--text-muted);
  }

  /* ===== appended global fixes (2026-09-04) ===== */
  /* one line + ellipsis without raising the bar's min-content (nowrap would widen the whole compose grid past a 320px viewport) */
  .bar h2 {
    min-width: 0;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    line-clamp: 1;
    overflow-wrap: anywhere;
  }

  /* native <select> needs an explicit background (Windows dark mode) */
  .row select {
    background-color: var(--surface-card);
  }

  /* compose: recipient row — the field may shrink, Cc/Bcc wrap under it when there is no room */
  @media (max-width: 480px) {
    .row.arow {
      flex-wrap: wrap;
      column-gap: var(--space-2);
      row-gap: 0;
    }

    .row.arow > :global(.awrap) {
      flex: 1 1 200px;
    }

    .cc-btn {
      padding: var(--space-2) var(--space-3);
      min-height: var(--control-md);
    }
  }

  /* below 375px the two Cc/Bcc buttons move to the next line together, never split around the field */
  @media (max-width: 374px) {
    .row.arow > :global(.awrap) {
      flex: 1 1 100%;
    }
  }

  /* compose toolbar (fixed at the bottom on phones): two rows → the form leaves room for both */
  @media (max-width: 480px) {
    .form {
      padding-bottom: calc(2 * var(--control-lg) + var(--space-4) + env(safe-area-inset-bottom));
    }
  }

  /* ===== component: svelte-1n46o8q (App shell) — docked compose chrome ===== */
  .dock {
    position: fixed;
    inset-block-end: 0;
    inset-inline-end: var(--space-6);
    inline-size: min(560px, calc(100vw - 2 * var(--space-6)));
    block-size: min(600px, calc(100dvh - 56px));
    z-index: var(--z-layer);
    display: grid;
    grid-template-rows: auto 1fr;
    min-block-size: 0;
    border: 1px solid var(--border-default);
    border-end-start-radius: 0;
    border-end-end-radius: 0;
    border-start-start-radius: var(--radius-lg);
    border-start-end-radius: var(--radius-lg);
    background: var(--surface-card);
    box-shadow: var(--shadow-xl);
    overflow: hidden;
  }

  .dock.min {
    block-size: auto;
  }

  .dock.min .dock-body {
    display: none;
  }

  .dock-bar {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: 0 var(--space-2) 0 var(--space-4);
    min-block-size: 44px;
    background: var(--surface-sunken);
    border-block-end: 1px solid var(--border-subtle);
  }

  .dock-title {
    flex: 1;
    min-inline-size: 0;
    color: var(--text-strong);
    font-weight: var(--weight-medium);
    font-size: var(--text-sm);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .dock-chev {
    display: grid;
    place-items: center;
  }

  .dock-chev.up {
    transform: rotate(180deg);
  }

  .dock-body {
    min-block-size: 0;
    overflow: hidden;
  }

  /* Compose opens full-page; the .tik-fullbtn (spec §1.5 item 2) switches to the small dock. */
  .dock.tik-full:not(.min) {
    inset: 0;
    inline-size: auto;
    block-size: auto;
    max-block-size: none;
    border: none;
    border-radius: 0;
    box-shadow: none;
  }

  .dock.tik-full:not(.min) .dock-bar {
    padding-block-start: env(safe-area-inset-top);
  }

  /* the compose header right below already says "رسالة جديدة" — don't print the title twice */
  .dock.tik-full:not(.min) .dock-title {
    visibility: hidden;
  }

  .tik-fullbtn {
    display: grid;
    place-items: center;
    flex: none;
    inline-size: var(--control-md);
    block-size: var(--control-md);
    padding: 0;
    border: none;
    border-radius: var(--radius-control);
    background: none;
    color: var(--text-muted);
    font: inherit;
    cursor: pointer;
  }

  .tik-fullbtn:hover {
    background: var(--surface-sunken);
    color: var(--text-strong);
  }

  .tik-fullbtn:active {
    background: var(--mail-pressed);
  }
</style>
